import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import {
  type ApolloQueryResult,
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import { InputAdornment, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import { FUNCTION_HUB_TEXT } from '../lang';
import {
  Alert,
  type AlertDetails,
  Button,
  Checkbox,
  Dialog,
  IconButton,
  Loading,
  RawSelect,
  type SelectOption,
  TextField,
} from '../../../../../../components';
import {
  type ChiefSupervisorFromValues,
  type SupervisorValue,
  useGetChiefSupervisors,
  useGetStaffInfoByNetID,
  useUpdateNomination,
} from '../../../../../Nomination/graphql';
import { SupervisorRoleTypes } from '../../../../../Nomination';
import { PAGE_TEXT } from '../../../../../Nomination/components/lang';
import { titleTypeOptions } from '../../../../../Application/components/ApplicationForm/AcademicReferee/options';
import { useAuth } from '../../../../../../contexts';
import { FundingSource } from '../../../../../Nomination/components/NominationForm/FundingModule/types';
import { DORPGS_CHANGE_SUP_FUNDING_STATUS } from '../../../../../ScholarshipManagement/constants/fundingStatus';
import { type RawFundingWithStatus } from '../../../../../Application/components/ApplicationViewList/graphql';

type ChiefSupervisorContentProps = {
  nominationId: string;
  fundingDetails: RawFundingWithStatus[];
  departmentLevel: boolean;
  refetch: () => Promise<ApolloQueryResult<any>>;
  handleCloseDialog: () => void;
  handleOpenAlert: (alertDetails: AlertDetails) => void;
};
const CHIEF_SUPERVISOR_TEXT = PAGE_TEXT.CHIEF_SUPERVISOR.CHIEF_SUPERVISOR;

export const ChiefSupervisorContent: React.FC<ChiefSupervisorContentProps> = (
  props: ChiefSupervisorContentProps,
) => {
  const {
    nominationId,
    fundingDetails,
    departmentLevel,
    handleCloseDialog,
    refetch: refetchLatestList,
    handleOpenAlert,
  } = props;
  const { handleLoading } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);

  // [RPG-1814] disable change supervisor for competitive awardees (Except HKPFS) for DoRPgS
  // [RPG-1815] GS can change supervisor for competitive awardees
  // here using departmentLevel(boolean) as a flag
  const shouldSkip: boolean =
    fundingDetails.some(
      f =>
        [
          FundingSource.POLYU_PRESIDENTIAL_PHD_FELLOWSHIP_SCHEME,
          FundingSource.INTERNATIONAL_PHD_FELLOWSHIP_SCHEME,
          FundingSource.POLYU_COMPETITIVE_PHD_FELLOWSHIP_SCHEME,
          FundingSource.DUAL_PHD_DEGREE_PROGRAMME,
        ].includes(f.fundingValue) &&
        f.fundingStatus !== null &&
        !DORPGS_CHANGE_SUP_FUNDING_STATUS.includes(f.fundingStatus),
    ) && departmentLevel;

  const {
    chiefSupervisor: initialChiefSupervisor,
    departmentOptions,
    coSupervisors,
    loading,
    error,
  } = useGetChiefSupervisors(nominationId, shouldSkip);

  const updateNomination = useUpdateNomination();
  const handleUpdateSupervisors = async (
    value: ChiefSupervisorFromValues,
  ): Promise<void> => {
    const otherSupervisors = coSupervisors.filter(
      co => co.netId !== value.netId,
    );

    await handleLoading(async () => {
      try {
        const chiefSup: SupervisorValue = {
          type: SupervisorRoleTypes.CHIEF_SUPERVISOR,
          ...value,
        };
        await updateNomination({
          variables: {
            id: nominationId,
            input: {
              // to keep co supervisor data (if any)
              supervisors: [chiefSup, ...otherSupervisors],
            },
          },
        });
        await refetchLatestList();

        handleOpenAlert({
          alertContent: 'Updated Chief Supervisor Information Successfully.',
          alertType: 'success',
        });
      } catch (e) {
        handleOpenAlert({
          alertContent:
            'Updated Chief Supervisor Information Failed.' +
            (e as Error).message,
          alertType: 'error',
        });
      }
    });
    setDialogOpen(false);
  };

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const handleSearchAndFill = async (): Promise<void> => {
    const searchId = formik.values.netId ?? '';
    await handleLoading(async () => {
      try {
        const res = await useGetStaffInfoByNetID(client, searchId);
        if (res !== null) {
          const fieldsToUpdate = {
            department: res?.department ?? '',
            email: res?.email ?? '',
            givenName: res?.givenName ?? '',
            surname: res?.surname ?? '',
            isDrcChair: res?.rightDRC ?? false,
            jobTitle: res?.title ?? '', // the title from backend, which means post
          };

          await formik.setValues({
            ...formik.values,
            ...fieldsToUpdate,
          });
        } else {
          handleOpenAlert({
            alertContent: 'No matching academic staff found.',
            alertType: 'warning',
          });
        }
      } catch (error) {
        handleOpenAlert({
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        });
      }
    });
  };

  if (error)
    return (
      <Alert
        openAlert
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: 'Error Occurs.' + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );

  const formik = useFormik<ChiefSupervisorFromValues>({
    // display the initial value in placeholder
    initialValues: {
      ...initialChiefSupervisor,
      netId: '',
      institution: '',
      title: '',
      jobTitle: '',
      givenName: '',
      surname: '',
      department: '',
      email: '',
    },
    validationSchema: object({
      netId: string().required('Required'),
      title: string().required('Required'),
      jobTitle: string().required('Required'),
      surname: string().required('Required'),
      givenName: string().required('Required'),
      department: string().required('Required'),
      email: string().email('Invalid email format').required('Required'),
    }),
    enableReinitialize: true,
    onSubmit: async value => {
      if (coSupervisors.some(i => i.netId === value.netId)) {
        setDialogOpen(true);
        return;
      }

      await handleUpdateSupervisors(value);
    },
  });

  // keep the order align with the supervisor team page
  const formData: Array<{
    label: string;
    fieldType: 'text' | 'select';
    options?: SelectOption[];
    fieldName: keyof ChiefSupervisorFromValues;
  }> = [
    {
      label: CHIEF_SUPERVISOR_TEXT.NET_ID,
      fieldType: 'text',
      fieldName: 'netId',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.DEPARTMENT,
      options: departmentOptions,
      fieldType: 'select',
      fieldName: 'department',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.GIVEN_NAME,
      fieldType: 'text',
      fieldName: 'givenName',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.SURNAME,
      fieldType: 'text',
      fieldName: 'surname',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.SUPERVISOR_TITLE,
      options: titleTypeOptions,
      fieldType: 'select',
      fieldName: 'title',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.JOB_TITLE,
      fieldType: 'text',
      fieldName: 'jobTitle',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.EMAIL,
      fieldType: 'text',
      fieldName: 'email',
    },
  ];

  useEffect(() => {
    if (!loading)
      formik.resetForm({
        values: {
          ...initialChiefSupervisor,
          netId: '',
          institution: '',
          title: '',
          jobTitle: '',
          givenName: '',
          surname: '',
          department: '',
          email: '',
        },
      });
  }, [loading]);

  const checkboxContent: Array<{
    label: string;
    fieldName: keyof ChiefSupervisorFromValues;
  }> = [
    {
      label: CHIEF_SUPERVISOR_TEXT.PREVIOUS_SUCCESSFUL_SUPERVISION,
      fieldName: 'previousSuccessfulSupervision',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.DSRC_CHAIR,
      fieldName: 'isDrcChair',
    },
    {
      label: CHIEF_SUPERVISOR_TEXT.DORPGS,
      fieldName: 'isDoRPgS',
    },
  ];

  return (
    <>
      <Dialog
        openDialog={dialogOpen}
        width={'860px'}
        dialogTitle={''}
        dialogAction={FUNCTION_HUB_TEXT.CONFIRM}
        cancelAction={FUNCTION_HUB_TEXT.CANCEL}
        onDialogAction={async () => {
          await handleUpdateSupervisors(formik.values);
        }}
        onDialogClose={() => {
          setDialogOpen(false);
        }}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {FUNCTION_HUB_TEXT.CHANGE_SUPERVISOR.REASSIGN}
        </Typography>
      </Dialog>
      <div className="min-w-[720px] px-[16px] py-[20px] bg-[#FAFAFA]">
        {shouldSkip ? (
          <div
            className="pt-[12px] font-semibold"
            dangerouslySetInnerHTML={{
              __html: CHIEF_SUPERVISOR_TEXT.HINT,
            }}
          />
        ) : (
          <div>
            {loading ? (
              <Loading />
            ) : (
              <form onSubmit={formik.handleSubmit} noValidate>
                <div className="border border-[1px] border-[#DFDFDF] bg-white my-4">
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4">
                      {formData.map((item, index) => {
                        const isNetIdSearch = item.fieldName === 'netId';

                        return (
                          <div key={index}>
                            {item.fieldType === 'select' ? (
                              <RawSelect
                                fullWidth
                                options={item.options ?? []}
                                label={item.label}
                                name={item.fieldName}
                                required
                                value={formik.values[item.fieldName] ?? ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder={
                                  initialChiefSupervisor[
                                    item.fieldName
                                  ] as string
                                }
                                error={
                                  formik.touched[item.fieldName] &&
                                  Boolean(formik.errors[item.fieldName])
                                }
                                helperText={
                                  formik.touched[item.fieldName] &&
                                  formik.errors[item.fieldName]
                                }
                              />
                            ) : (
                              <TextField
                                fullWidth
                                type="text"
                                required
                                label={item.label}
                                name={item.fieldName}
                                value={formik.values[item.fieldName]}
                                InputLabelProps={{ shrink: true }}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                  formik.touched[item.fieldName] &&
                                  Boolean(formik.errors[item.fieldName])
                                }
                                placeholder={
                                  initialChiefSupervisor[
                                    item.fieldName
                                  ] as string
                                }
                                helperText={
                                  formik.touched[item.fieldName] &&
                                  formik.errors[item.fieldName]
                                }
                                InputProps={{
                                  endAdornment: isNetIdSearch ? (
                                    <InputAdornment position="end">
                                      <IconButton
                                        icon={<Search />}
                                        onClick={handleSearchAndFill}
                                        disabled={!formik.values.netId}
                                        color="primary"
                                        title="Search by Net ID"
                                      />
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex flex-row gap-2 mt-2">
                      {checkboxContent.map((item, index) => (
                        <Checkbox
                          key={index}
                          label={item.label}
                          name={item.fieldName}
                          value={formik.values[item.fieldName] as boolean}
                          onChange={formik.handleChange}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-row justify-center gap-[20px]">
                  <Button
                    variant="contained"
                    className="!normal-case"
                    type="submit"
                  >
                    {FUNCTION_HUB_TEXT.SAVE}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCloseDialog}
                    className="!normal-case"
                  >
                    {FUNCTION_HUB_TEXT.CANCEL}
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </>
  );
};
